import servicesConfig, { CustomAxiosHeaders } from '../config/services'

import { AxiosResponse } from 'axios'
import config from '../config'
import { requests } from '.'

// Configure request setup
const serviceName = 'IntegrationOauthService'
const host = config.restApiUrl
const opts: CustomAxiosHeaders = { host, serviceName }
const request = (addlOpts?: CustomAxiosHeaders) => {
  return requests(Object.assign({}, opts, addlOpts))
}

// Helper function to handle response (similar to BaseService.responseBody)
const responseBody = (res: AxiosResponse) => res.data

const IntegrationOauthService = {
  // Method to fetch integration details
  fetchIntegrationDetails: async (appId: string) => {
    try {
      if (!appId) {
        return null
      }
      const response = await request().get(`/integrations/public/${appId}`).then(responseBody)
      return response?.integration || null
    } catch (error) {
      console.error('Error fetching integration details:', error)
      return null
    }
  },
  // Method to initiate OAuth authorization
  initiateOAuthAuthorization: async (params: { appId: string; locationId: string; companyId?: string }) => {
    const { appId, locationId, companyId } = params

    if (!appId || !locationId) {
      console.error('Missing required parameters for OAuth authorization')
      return false
    }

    try {
      // First, fetch integration details to get the client ID
      const integrationDetails = await IntegrationOauthService.fetchIntegrationDetails(appId)
      if (!integrationDetails || !integrationDetails.clientKeys || integrationDetails.clientKeys.length === 0) {
        console.error('No client keys found for integration')
        return false
      }

      const clientId = integrationDetails.clientKeys[0].id

      // Make the POST request to initiate OAuth authorization
      const response = await request().post(`/workflows-marketplace/integration/${appId}/install?locationId=${locationId}`, {
        clientId,
        companyId,
      })
      return response.status > 200
    } catch (error) {
      console.error('Error initiating OAuth authorization:', error)
      return false
    }
  },
  // Method to fetch OAuth URL for the app
  fetchOAuthUrl: async (params: { appId: string; locationId: string }) => {
    const { appId, locationId } = params

    if (!appId || !locationId) {
      console.error('Missing required parameters for fetching OAuth URL')
      return null
    }

    try {
      const url = `${config.restApiUrl}/oauth/clients/${appId}/authentication/oauth2/location/${locationId}/url`
      const response = await request().get(url).then(responseBody)
      console.log('OAuth URL fetched successfully:', response)
      return response?.url || null
    } catch (error) {
      console.error('Error fetching OAuth URL:', error)
      return null
    }
  },
  // Method to invalidate cache for an integration
  invalidateCache: (appId: string, locationId: string) => {
    // This method can be used to clear any cached data for the integration
    // Implementation depends on your caching strategy
    console.log(`Invalidating cache for app ${appId} and location ${locationId}`)
  },
  // Helper method to get app ID from app name
  getAppId: async (appName: string) => {
    try {
      const response = await request()
        .get(`/integrations/public/search`, {
          params: { name: appName },
        })
        .then(responseBody)
      return response?.integration?.id || null
    } catch (error) {
      console.error('Error fetching app ID:', error)
      return null
    }
  },
  // Method to check connection status
  checkConnectionStatus: async (params: { appId: string; locationId: string; companyId: string }) => {
    const { appId, locationId, companyId } = params

    if (!appId || !locationId || !companyId) {
      console.error('Missing required parameters for checking connection status')
      return { isConnected: false, isDisconnected: false }
    }

    try {
      const params = {
        limit: 1,
        skip: 0,
        companyId,
        appId,
        isInstalled: true,
        locationId,
      }

      const response = await request().get('/oauth/installedLocations', { params }).then(responseBody)
      if (response?.currentLocation && response?.currentLocation?.externalAuthDetails) {
        const integration = response?.currentLocation?.externalAuthDetails || {}

        // Check if integration is installed but externalAuthDetails is empty
        if (!integration || Object.keys(integration).length === 0) {
          console.log('Integration is installed but externalAuthDetails is empty - disconnected state')
          return { isConnected: false, isDisconnected: true }
        }

        // Check if integration is installed and has externalAuthDetails
        // OAuth-style connection: valid token present
        if (integration.validToken) {
          return { isConnected: true, validToken: true, isDisconnected: false }
        }

        // Basic auth-style connection: keyChain present means connected
        if (integration.type === 'basic' && typeof integration.keyChain === 'string' && integration.keyChain.trim().length > 0) {
          return { isConnected: true, validToken: false, hasKeyChain: true, isDisconnected: false }
        }

        return { isConnected: false, isDisconnected: false }
      } else {
        return { isConnected: false, isDisconnected: false }
      }
    } catch (error) {
      console.error('Error checking connection status:', error)
      return { isConnected: false, isDisconnected: false }
    }
  },
  // Method to disconnect integration
  disconnectIntegration: async (params: { appId: string; locationId: string; companyId: string }) => {
    const { appId, locationId, companyId } = params

    if (!appId || !locationId || !companyId) {
      throw new Error('Missing required parameters for disconnecting integration')
    }

    const body = {
      companyId: companyId,
      approvedLocations: [locationId],
      excludedLocations: [],
      approveAllLocations: false,
      unInstallAgency: false,
    }

    await request().delete(`/marketplace/app/${appId}/installations`, { data: body })
  },
  // Method to fetch integration apps from workflows-marketplace
  fetchIntegrationApps: async (locationId: string) => {
    if (!locationId) {
      console.error('Missing locationId for fetching integration apps')
      return []
    }

    try {
      const response = await request()
        .get(`/workflows-marketplace/integration-apps`, {
          params: { locationId },
        })
        .then(responseBody)
      return response || []
    } catch (error) {
      console.error('Error fetching integration apps:', error)
      return []
    }
  },
  // Method to submit API key for basic authentication
  submitBasicAuthApiKey: async (params: { appId: string; locationId: string; apiKey: string }) => {
    const { appId, locationId, apiKey } = params

    if (!appId || !locationId || !apiKey) {
      console.error('Missing required parameters for submitting API key')
      return { success: false, status: 400, message: 'Missing required parameters' }
    }

    try {
      const url = `${config.restApiUrl}/oauth/clients/${appId}/authentication/location/${locationId}`
      const response = await request().patch(url, { apiKey })
      // If no error is thrown, the request was successful
      // Axios throws errors for status codes >= 400, so if we reach here, it's successful
      const status = (response as any)?.status || (response as any)?.response?.status || 200
      return { success: true, status }
    } catch (error) {
      console.error('Error submitting API key:', error)
      const status = (error as any)?.response?.status
      const message = (error as any)?.response?.data?.message || (error as any)?.response?.data?.error || (error as any)?.message
      return { success: false, status, message }
    }
  },
}

export default IntegrationOauthService
